import { DatePicker } from '@mui/lab';
import { FormControlLabel, Grid, SelectChangeEvent, useEventCallback } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { FormikErrors, FormikTouched } from 'formik';
import ListNumberConceived from '../list/list-number-conceived';
import { SectionVM } from 'civil-status/view-models/step-form';
import { Checkbox } from '@mui/material';
import { ConfigurationType } from 'civil-status/constants/configuration-type';
import ListAnsc from '../../../../../../shared/list-ansc';
import { ItemVM } from 'shell/view-models/item';

interface ActConsentProps {
  disabled?: boolean;
  values?: SectionVM;
  errors?: FormikErrors<SectionVM>;
  touched?: FormikTouched<SectionVM>;
  onChange: (key: keyof SectionVM, value: SectionVM[keyof SectionVM]) => void;
  controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence: (
    typologyConsentAndConfiguration?: ItemVM,
    typeDeclaration?: string,
    parentRecognition?: string,
    isIncestuousSonSentence?: boolean
  ) => void;
  configuration?: string;
}

const RecognitionBeforBirthComponent = ({
  disabled = false,
  values,
  errors,
  touched,
  onChange,
  controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence,
  configuration,
}: ActConsentProps): React.ReactElement => {
  const handleListNumberConceivedChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('conceivedMonth', event.target.value);
  });

  const handleListPresentationDocument = useEventCallback((item?: ItemVM) => {
    onChange('presentationDocument', item?.id);
  });

  useEffect(() => {
    controllTypologyConsentTypeDeclarationParentRecognitionIsIncestuousSonSentence(
      undefined,
      undefined,
      undefined,
      values?.isIncestuousSonSentence
    );
  }, [values?.isIncestuousSonSentence]);

  const isBaseRicNse = useMemo(() => {
    return configuration === ConfigurationType.BASERICNSE;
  }, [configuration]);

  return (
    <Grid container spacing={2} item>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="Certificato sanitario rilasciato da *"
          aria-label="Certificato sanitario rilasciato da "
          id="certificateReleaseFrom"
          name="certificateReleaseFrom"
          fullWidth
          disabled={disabled}
          helperText={errors?.certificateReleaseFrom}
          error={touched?.certificateReleaseFrom && Boolean(errors?.certificateReleaseFrom)}
          type="text"
          variant="outlined"
          value={values?.certificateReleaseFrom || ''}
          onChange={(event) => {
            onChange('certificateReleaseFrom', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Certificato sanitario data *"
          aria-label="Certificato sanitario data"
          value={values?.certificateDate || null}
          onChange={(date) => {
            onChange('certificateDate', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="certificateDate"
              fullWidth
              helperText={errors?.certificateDate}
              error={touched?.certificateDate && Boolean(errors?.certificateDate)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListNumberConceived
          value={values?.conceivedMonth || ''}
          onChange={handleListNumberConceivedChange}
          disabled={disabled}
          error={Boolean(errors?.conceivedMonth) && touched?.conceivedMonth}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ListAnsc
          label="Presentazione documenti"
          idDominio="36"
          value={values?.presentationDocument || ''}
          onChange={handleListPresentationDocument}
          disabled={disabled}
          error={Boolean(errors?.presentationDocument) && touched?.presentationDocument}
          required
        />
      </Grid>
      {isBaseRicNse ? undefined : (
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Checkbox
                id="isIncestuousSonSentence"
                checked={values?.isIncestuousSonSentence || false}
                onChange={() => {
                  onChange('isIncestuousSonSentence', !values?.isIncestuousSonSentence);
                }}
                inputProps={{ 'aria-label': 'Sentenza figlio incestuoso' }}
              />
            }
            label="Sentenza figlio incestuoso"
          />
        </Grid>
      )}
    </Grid>
  );
};

RecognitionBeforBirthComponent.displayName = 'Form Recognition Befor Birth component';

const RecognitionBeforBirth = memo(RecognitionBeforBirthComponent);

export default RecognitionBeforBirth;
